import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import CurrencyField from 'components/CurrencyField'
import useAuth from 'hooks/useAuth'
import { useClosePlan, useCreatePlan } from 'hooks/usePlans'
import { useSnackbar } from 'notistack'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import type { Period } from 'types/periods'
import { parsePeriodDate } from 'types/periods'
import type { ClosePlanParams } from 'types/comp-plan'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { DATE_FORMAT } from 'types/periods'

dayjs.extend(utc)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

interface PlanToUpdate {
  id: string
  revision: number
  userId: string
  templateId: string
  employeeFirstName: string
  employeeLastName: string
  baseSalary: number
  position: string
  periodProration: number
  compensationModel: string
  variablePercent: number
  variableCap: number
  periodId: string
  organizationId: string
  rootOrganizationId: string
  rootPeriodId: string
}

interface UpdateCompensationDialogProps {
  open: boolean
  onClose: () => void
  onUpdate: () => void
  plans: PlanToUpdate[]
  periods: Period[]
}

type Step = 'edit' | 'review'

const UpdateCompensationDialog: FC<UpdateCompensationDialogProps> = ({ open, onClose, onUpdate, plans, periods }) => {
  const { enqueueSnackbar } = useSnackbar()
  const { token } = useAuth()
  const { closePlan } = useClosePlan(token!)
  const { createPlan } = useCreatePlan(token!)

  const [activeStep, setActiveStep] = useState<Step>('edit')
  const [newPlanStartDate, setNewPlanStartDate] = useState<Dayjs | null>(null)
  const [editablePlansData, setEditablePlansData] = useState<Record<string, { newBaseSalary: string }>>({})
  const [isProcessing, setIsProcessing] = useState(false)

  // Helper to get plan key
  const getPlanKey = (planId: string, revision: number) => `${planId}-${revision}`

  // Get the period for the selected plans (all plans are in the same period)
  const currentPeriod = useMemo(() => {
    if (!plans.length || !periods.length) return null

    const periodId = plans[0].periodId

    // Find the period in the periods array
    const findPeriod = (periodList: Period[]): Period | null => {
      for (const period of periodList) {
        if (period.id === periodId) return period
        if (period.children?.length) {
          const found = findPeriod(period.children)
          if (found) return found
        }
      }
      return null
    }

    return findPeriod(periods)
  }, [plans, periods])

  // Parse period dates for validation
  const periodStartDate = useMemo(() => {
    if (!currentPeriod?.startDate) return null
    return parsePeriodDate(currentPeriod.startDate)
  }, [currentPeriod])

  const periodEndDate = useMemo(() => {
    if (!currentPeriod?.endDate) return null
    return parsePeriodDate(currentPeriod.endDate)
  }, [currentPeriod])

  // Helper function to check if a date should be disabled (outside period range)
  const shouldDisableDate = useCallback(
    (date: Dayjs): boolean => {
      if (!periodStartDate || !periodEndDate) return true
      const checkDate = date.toDate()
      return checkDate < periodStartDate || checkDate > periodEndDate
    },
    [periodStartDate, periodEndDate]
  )

  // Validate that a date is within the period boundaries
  const isDateValid = useCallback(
    (date: Dayjs | null) => {
      if (!date || !periodStartDate || !periodEndDate) return false
      const checkDate = date.toDate()
      return checkDate >= periodStartDate && checkDate <= periodEndDate
    },
    [periodStartDate, periodEndDate]
  )

  // Reset states when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setActiveStep('edit')
      setNewPlanStartDate(null)
      setEditablePlansData({})
    } else if (open && plans.length > 0) {
      // Initialize with current base salaries
      const initialData: Record<string, { newBaseSalary: string }> = {}
      plans.forEach((plan) => {
        const planKey = getPlanKey(plan.id, plan.revision)
        initialData[planKey] = {
          newBaseSalary: plan.baseSalary.toString(),
        }
      })
      setEditablePlansData(initialData)

      // Set default start date to today if within period, otherwise period start
      const today = dayjs()
      if (isDateValid(today)) {
        setNewPlanStartDate(today)
      } else if (periodStartDate) {
        setNewPlanStartDate(dayjs(periodStartDate))
      }
    }
  }, [open, plans, isDateValid, periodStartDate])

  const handleNext = () => {
    if (activeStep === 'edit') {
      setActiveStep('review')
    }
  }

  const handleBack = () => {
    if (activeStep === 'review') {
      setActiveStep('edit')
    }
  }

  const handleConfirm = async () => {
    if (!newPlanStartDate) {
      enqueueSnackbar('Missing required data', { variant: 'error' })
      return
    }

    if (!isDateValid(newPlanStartDate)) {
      enqueueSnackbar('Start date must be within the period boundaries', { variant: 'error' })
      return
    }

    setIsProcessing(true)

    try {
      // New plan starts on the selected date at start of day
      const newEffectiveDate = newPlanStartDate.format('YYYY-MM-DD') + 'T00:00:00.000Z'
      // Old plan ends on the day before at start of day
      const dayBefore = dayjs(newPlanStartDate).subtract(1, 'day')
      const endDate = dayBefore.format('YYYY-MM-DD') + 'T00:00:00.000Z'

      let closeFailCount = 0
      let createSuccessCount = 0
      let createFailCount = 0

      // Step 1: Close all existing plans with the effective date
      for (const plan of plans) {
        try {
          await closePlan({
            planId: plan.id,
            endDate: endDate,
            rootOrganizationId: plan.rootOrganizationId,
          } as ClosePlanParams)
        } catch (error) {
          console.error(`Error closing plan for ${plan.employeeFirstName} ${plan.employeeLastName}:`, error)
          closeFailCount++
        }
      }

      // Step 2: Create new plans with updated base salary
      // CRITICAL: Load each plan's data individually to preserve their unique templates, components, and allocations
      for (const plan of plans) {
        try {
          const planKey = getPlanKey(plan.id, plan.revision)
          const planData = editablePlansData[planKey] || { newBaseSalary: plan.baseSalary.toString() }

          // Fetch THIS specific plan's full data (not the first plan's data!)
          const apiServerUrl = process.env.REACT_APP_API_SERVER_URL
          const response = await fetch(
            `${apiServerUrl}compensation/plans/user/${plan.userId}/${plan.id}/${plan.revision}`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          )

          if (!response.ok) {
            throw new Error(`Failed to fetch plan data: ${response.status}`)
          }

          const individualPlanData = await response.json()

          if (!individualPlanData?.plan) {
            throw new Error('Failed to load plan data')
          }

          // Get template info from THIS plan
          const templateId = individualPlanData.plan.templateId || plan.templateId
          const templateRevision = individualPlanData.plan.templateRevision || 1

          // Get all component IDs from THIS plan
          const planComponentIds =
            individualPlanData.components
              ?.map((c: any) => c.id)
              .filter((id: string | undefined): id is string => !!id) || []

          // Get all existing targets for this user from THIS plan
          const existingTargets = (individualPlanData.targets || [])
            .filter((target: any) => target.userId === plan.userId)
            .map((target: any) => ({
              componentId: target.componentId,
              periodId: target.periodId || plan.periodId,
              value: target.value?.toString() || '',
              slug: target.slug || '',
              userId: plan.userId,
              organizationId: target.organizationId || plan.organizationId,
              rootOrganizationId: target.rootOrganizationId || plan.rootOrganizationId,
              rootPeriodId: target.rootPeriodId || plan.rootPeriodId,
              label: individualPlanData.components?.find((c: any) => c.id === target.componentId)?.label || '',
            }))
            .filter((target: any) => target.componentId)

          // Get variable allocations from THIS plan (CRITICAL - each plan has different allocations!)
          const variableAllocations = individualPlanData.plan.variableAllocations || []

          // Build the plan creation request with THIS plan's data
          const createPlanRequest = {
            plan: {
              userId: plan.userId,
              templateId: templateId || undefined,
              templateRevision,
              organizationId: plan.organizationId,
              rootOrganizationId: plan.rootOrganizationId,
              rootPeriodId: plan.rootPeriodId,
              periodId: plan.periodId,
              periodProration: plan.periodProration,
              position: plan.position,
              compensationModel: plan.compensationModel,
              baseSalary: parseFloat(planData.newBaseSalary) || 0,
              variablePercent: plan.variablePercent,
              variableCap: plan.variableCap,
              variableAllocations: variableAllocations,
              effectiveDate: newEffectiveDate,
            },
            components: planComponentIds,
            targets: existingTargets,
          }

          await createPlan(createPlanRequest)
          createSuccessCount++
        } catch (error) {
          console.error(`Error creating plan for ${plan.employeeFirstName} ${plan.employeeLastName}:`, error)
          createFailCount++
        }
      }

      // Show appropriate success/error message
      if (closeFailCount === 0 && createFailCount === 0) {
        enqueueSnackbar(`Successfully updated ${createSuccessCount} plan${createSuccessCount !== 1 ? 's' : ''}`, {
          variant: 'success',
        })
      } else {
        const messages = []
        if (closeFailCount > 0) {
          messages.push(`Failed to close ${closeFailCount} plan${closeFailCount !== 1 ? 's' : ''}`)
        }
        if (createFailCount > 0) {
          messages.push(`Failed to create ${createFailCount} plan${createFailCount !== 1 ? 's' : ''}`)
        }
        enqueueSnackbar(messages.join(', '), { variant: 'error' })
      }

      // Trigger data refresh
      onUpdate()
      onClose()
    } catch (error) {
      console.error('Error updating compensation:', error)
      enqueueSnackbar('Failed to update compensation', { variant: 'error' })
    } finally {
      setIsProcessing(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const renderEditStep = () => {
    const newStartDate = newPlanStartDate ? newPlanStartDate.format('MMM D, YYYY') : '[new plan start date]'
    const oldEndDate = newPlanStartDate
      ? newPlanStartDate.subtract(1, 'day').format('MMM D, YYYY')
      : '[day before new plan start]'

    const periodRange =
      periodStartDate && periodEndDate
        ? `${dayjs(periodStartDate).format('MMM D, YYYY')} - ${dayjs(periodEndDate).format('MMM D, YYYY')}`
        : 'Unknown'

    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          Update compensation for the selected plans. The existing plans will be finalized on{' '}
          <strong>{oldEndDate}</strong> and the new plans will begin on <strong>{newStartDate}</strong>.
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Stack spacing={1}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                label="New Plan Start Date *"
                value={newPlanStartDate}
                onChange={(newValue: Dayjs | null) => {
                  if (newValue) {
                    const dateString = newValue.format('YYYY-MM-DD')
                    const utcDate = dayjs.utc(dateString)
                    setNewPlanStartDate(utcDate)
                  } else {
                    setNewPlanStartDate(null)
                  }
                }}
                format={DATE_FORMAT}
                shouldDisableDate={shouldDisableDate}
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    fullWidth: true,
                    InputProps: {
                      readOnly: true,
                    },
                    InputLabelProps: {
                      shrink: true,
                      sx: {
                        '&.Mui-focused': {
                          color: (theme) => theme.palette.primary.main,
                        },
                      },
                    },
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: (theme) => theme.shape.borderRadius * 5.25,
                        '& fieldset': {
                          borderColor: (theme) => theme.palette.primary.main,
                        },
                        '&:hover fieldset': {
                          borderColor: (theme) => theme.palette.primary.dark,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: (theme) => theme.palette.secondary.main,
                        },
                      },
                    },
                    helperText: `Must be within period: ${periodRange}`,
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>
        </Box>

        <TableContainer>
          <Table size="small" aria-label="update-compensation-table">
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  Employee
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  Current Annual
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  Current Period
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  New Annual
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  New Period
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => {
                const planKey = getPlanKey(plan.id, plan.revision)
                const planData = editablePlansData[planKey] || { newBaseSalary: '0' }
                const newBaseSalary = parseFloat(planData.newBaseSalary) || 0

                return (
                  <TableRow key={planKey}>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {plan.employeeFirstName} {plan.employeeLastName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatCurrency(plan.baseSalary)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatCurrency(plan.baseSalary * plan.periodProration)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Box sx={{ width: '80%', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
                        <CurrencyField
                          value={planData.newBaseSalary}
                          onChange={(value) =>
                            setEditablePlansData((prev) => ({
                              ...prev,
                              [planKey]: { newBaseSalary: value },
                            }))
                          }
                          name={`newBaseSalary-${planKey}`}
                          currency="USD"
                          currencyDecimals={0}
                          variant="outlined"
                          pill
                          shrink
                          style={{ width: '100%' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatCurrency(newBaseSalary * plan.periodProration)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  const renderReviewStep = () => {
    const newStartDate = newPlanStartDate ? newPlanStartDate.format('MMM D, YYYY') : '[new plan start date]'
    const oldEndDate = newPlanStartDate
      ? newPlanStartDate.subtract(1, 'day').format('MMM D, YYYY')
      : '[day before new plan start]'

    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Warning:</strong> {plans.length} plan{plans.length !== 1 ? 's' : ''} will be finalized on{' '}
          <strong>{oldEndDate}</strong> and new plans will start on <strong>{newStartDate}</strong>. This action cannot
          be undone.
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>New Plan Start Date:</strong> {newPlanStartDate ? newPlanStartDate.format(DATE_FORMAT) : ''}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Old Plan End Date:</strong> {oldEndDate}
          </Typography>
        </Box>

        <TableContainer>
          <Table size="small" aria-label="review-compensation-table">
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  Employee
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  Current Annual
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  Current Period
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  New Annual
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                  New Period
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => {
                const planKey = getPlanKey(plan.id, plan.revision)
                const planData = editablePlansData[planKey] || { newBaseSalary: '0' }
                const newBaseSalary = parseFloat(planData.newBaseSalary) || 0

                return (
                  <TableRow key={planKey}>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {plan.employeeFirstName} {plan.employeeLastName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatCurrency(plan.baseSalary)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatCurrency(plan.baseSalary * plan.periodProration)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatCurrency(newBaseSalary)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatCurrency(newBaseSalary * plan.periodProration)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: 800,
        },
      }}
    >
      <DialogTitle color="primary">
        Update Compensation for {plans.length} Plan{plans.length !== 1 ? 's' : ''}
      </DialogTitle>
      <DialogContent>
        {activeStep === 'edit' && renderEditStep()}
        {activeStep === 'review' && renderReviewStep()}
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <Button
          onClick={onClose}
          disabled={isProcessing}
          variant="contained"
          color="warning"
          sx={{ marginRight: 'auto' }}
        >
          Cancel
        </Button>
        {activeStep !== 'edit' && (
          <Button onClick={handleBack} disabled={isProcessing} variant="outlined" color="primary">
            Back
          </Button>
        )}
        <Button
          onClick={activeStep === 'review' ? handleConfirm : handleNext}
          color="primary"
          variant="contained"
          disabled={!newPlanStartDate || !isDateValid(newPlanStartDate) || isProcessing}
          startIcon={isProcessing ? <CircularProgress size={20} /> : undefined}
          sx={{ ml: 1 }}
        >
          {activeStep === 'edit' ? 'Review' : 'Update Plans'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UpdateCompensationDialog
